import { FC, useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
    GET_SAMPLE_CONTACTS,
    GetSampleContactsQuery,
    UPDATE_SAMPLE_CONTACT_CONVERSATION,
    CREATE_SAMPLE_CONTACT,
    DELETE_SAMPLE_CONTACT,
    Order_By,
} from '@zaplify/graphql';
import { Card, CardContent } from '@shadcn/ui/components/ui/card';
import { Button } from '@shadcn/ui/components/ui/button';
import { Input } from '@shadcn/ui/components/ui/input';
import { Label } from '@shadcn/ui/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@shadcn/ui/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shadcn/ui/components/ui/select';
import { ChevronLeft, ChevronRight, MessageCircle, Clock, User, Edit3, Save, X, Plus } from 'lucide-react';
import { AdminHeader } from './components/admin-header';
import { getZaplifySdk } from '@zaplify/sdk';
import { Direction } from '@zaplify/campaigns';

interface Message {
    timestamp: string;
    sender: string;
    recipients: { email: string }[];
    text: string;
    subject?: string;
    direction?: string;
}

export interface SampleContact {
    id: string;
    contact: {
        email: string;
        externalId: string;
    };
    conversation?: {
        messages: Message[];
    }; // New conversation structure
    createdAt: string;
    updatedAt: string;
}

// Helper function to parse JSONB messages from database
const parseContactMessages = (rawMessages: string[]): Message[] => {
    if (!rawMessages || rawMessages.length === 0) return [];

    try {
        return rawMessages.map((jsonString) => JSON.parse(jsonString));
    } catch (error) {
        console.error('Failed to parse messages:', error);
        return [];
    }
};

// Helper function to format messages for JSONB storage
const formatMessagesForJsonb = (messages: Message[]): string => {
    const messagesAsJsonStrings = messages.map((message) => `"${JSON.stringify(message).replace(/"/g, '\\"')}"`);
    return `{ ${messagesAsJsonStrings.join(', ')} }`;
};

// Helper to filter out lines starting with '>'
const filterBlockquoteLines = (text: string) =>
    text
        .split('\n')
        .filter((line) => !line.trim().startsWith('>'))
        .join('\n');

export const SampleLibraryContent: FC<{
    onSelectedContactChange?: (contact: SampleContact | null) => void;
}> = ({ onSelectedContactChange }) => {
    const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [editingMessage, setEditingMessage] = useState<{
        contactId: string;
        messageIndex: number;
        text: string;
        subject?: string;
    } | null>(null);

    const [showCreateContactModal, setShowCreateContactModal] = useState(false);
    const [showAddMessageModal, setShowAddMessageModal] = useState(false);
    const [newContactForm, setNewContactForm] = useState({
        email: '',
        firstName: '',
        lastName: '',
    });
    const [newMessageForm, setNewMessageForm] = useState({
        text: '',
        subject: '',
        sender: 'me', // 'me' or 'recipient'
    });
    const pageSize = 50; // Load more contacts for better UX
    const offset = (page - 1) * pageSize;

    const { data, loading, error, refetch } = useQuery<GetSampleContactsQuery>(GET_SAMPLE_CONTACTS, {
        variables: { limit: pageSize, offset, orderBy: [{ updatedAt: Order_By.Desc }] },
        errorPolicy: 'all',
        context: {
            headers: {
                'x-hasura-role': 'GLOBAL_ADMIN',
            },
        },
    });

    const [updateSampleContactConversation] = useMutation(UPDATE_SAMPLE_CONTACT_CONVERSATION, {
        context: {
            headers: {
                'x-hasura-role': 'GLOBAL_ADMIN',
            },
        },
    });

    const [createSampleContact] = useMutation(CREATE_SAMPLE_CONTACT, {
        context: {
            headers: {
                'x-hasura-role': 'GLOBAL_ADMIN',
            },
        },
    });

    const [deleteSampleContact] = useMutation(DELETE_SAMPLE_CONTACT, {
        context: {
            headers: {
                'x-hasura-role': 'GLOBAL_ADMIN',
            },
        },
    });

    const contacts = (data?.SampleContacts as SampleContact[]) || [];
    const selectedContactRaw = contacts.find((contact) => contact.id === selectedContactId);

    // Parse the selected contact's messages from conversation or fallback to legacy messages
    const selectedContact = selectedContactRaw
        ? {
              ...selectedContactRaw,
              messages: selectedContactRaw.conversation?.messages,
          }
        : null;

    // Notify parent component when selected contact changes
    useEffect(() => {
        onSelectedContactChange?.(selectedContact);
    }, [selectedContact, onSelectedContactChange]);
    const totalContacts = data?.SampleContacts_aggregate?.aggregate?.count || 0;
    const totalPages = Math.ceil(totalContacts / pageSize);

    const formatTime = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

        if (diffInDays === 0) {
            return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        } else if (diffInDays < 7) {
            return date.toLocaleDateString('en-US', { weekday: 'short' });
        } else {
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
    };

    const formatMessageTime = (dateString: string) => {
        return new Date(dateString).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getLastMessage = (contact: SampleContact) => {
        // Try conversation messages first, then fallback to legacy messages
        const messages = contact.conversation?.messages;
        if (!messages || messages.length === 0) return null;
        return [...messages].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0];
    };

    const getContactName = (
        contact:
            | SampleContact
            | {
                  id: string;
                  contact: { email: string; externalId: string };
                  messages: Message[];
                  createdAt: string;
                  updatedAt: string;
              }
    ) => {
        return contact.contact.email.split('@')[0] || 'Unknown';
    };

    const isMessageFromMe = (message: Message, contactEmail: string) => {
        // Check if message direction is explicitly outbound
        if (message.direction === Direction.OUT) return true;

        // Default to inbound
        return false;
    };

    const handlePreviousPage = () => {
        if (page > 1) {
            setPage(page - 1);
        }
    };

    const handleNextPage = () => {
        if (page < totalPages) {
            setPage(page + 1);
        }
    };

    const handleSaveMessage = async () => {
        if (!editingMessage || !selectedContact) return;

        try {
            // Create a copy of the messages array
            const updatedMessages = [...selectedContact.messages];

            // Update the specific message
            const updatedMessage = {
                ...updatedMessages[editingMessage.messageIndex],
                text: editingMessage.text,
                ...(editingMessage.subject !== undefined && { subject: editingMessage.subject }),
            };

            updatedMessages[editingMessage.messageIndex] = updatedMessage;

            // Convert messages to proper JSON format
            const cleanMessages = updatedMessages.map((message) => ({
                timestamp: message.timestamp,
                sender: message.sender || '',
                recipients: Array.isArray(message.recipients) ? message.recipients : [],
                text: message.text || '',
                ...(message.subject && { subject: message.subject }),
                ...(message.direction && { direction: message.direction }),
            }));

            console.log('Original messages:', selectedContact.messages);
            console.log('Clean messages before sending:', cleanMessages);

            // Update conversation in the database
            await updateSampleContactConversation({
                variables: {
                    id: selectedContact.id,
                    conversation: {
                        messages: cleanMessages,
                    },
                },
            });

            // Refetch to ensure UI is updated
            await refetch();

            // Close the editor
            setEditingMessage(null);
        } catch (error) {
            console.error('Failed to update message:', error);
        }
    };

    const handleCancelEdit = () => {
        setEditingMessage(null);
    };

    const handleCreateContact = async () => {
        if (!newContactForm.email.trim()) return;

        try {
            const contactData = {
                email: newContactForm.email.trim(),
                externalId: `manual-${Date.now()}`,
                ...(newContactForm.firstName.trim() && { firstName: newContactForm.firstName.trim() }),
                ...(newContactForm.lastName.trim() && { lastName: newContactForm.lastName.trim() }),
            };

            console.log('Creating new contact:', contactData);

            // Create the contact in the database
            const result = await createSampleContact({
                variables: {
                    contact: contactData,
                    conversation: {
                        messages: [], // Empty messages array for new contact
                    },
                },
            });

            console.log('Contact created successfully:', result.data);

            // Reset form and close modal
            setNewContactForm({ email: '', firstName: '', lastName: '' });
            setShowCreateContactModal(false);

            // Reset to page 1 since new contact will appear at the top
            setPage(1);

            // Refetch the data to show the new contact
            await refetch({ limit: pageSize, offset: 0 });

            // Set the new contact as selected so user can see it immediately
            if (result.data?.insert_SampleContacts_one?.id) {
                setSelectedContactId(result.data.insert_SampleContacts_one.id);
            }
        } catch (error) {
            console.error('Failed to create contact:', error);
        }
    };

    const handleAddMessage = async () => {
        if (!selectedContact || !newMessageForm.text.trim()) return;

        try {
            const newMessage = {
                timestamp: new Date().toISOString(),
                sender: newMessageForm.sender === 'me' ? 'me@example.com' : selectedContact.contact.email,
                recipients:
                    newMessageForm.sender === 'me'
                        ? [{ email: selectedContact.contact.email }]
                        : [{ email: 'me@example.com' }],
                text: newMessageForm.text.trim(),
                direction: newMessageForm.sender === 'me' ? Direction.OUT : Direction.IN,
                ...(newMessageForm.subject.trim() && { subject: newMessageForm.subject.trim() }),
            };

            // Add the new message to the existing messages
            const updatedMessages = [...(selectedContact.messages || []), newMessage];

            // Clean and update messages for JsonB (plain objects)
            const cleanMessages = updatedMessages.map((message) => ({
                timestamp: message.timestamp,
                sender: message.sender || '',
                recipients: Array.isArray(message.recipients) ? message.recipients : [],
                text: message.text || '',
                ...(message.subject && { subject: message.subject }),
                ...(message.direction && { direction: message.direction }),
            }));

            // Format messages for PostgreSQL JSONB array: each message as a separate JSON string
            const postgresJsonbArray = formatMessagesForJsonb(cleanMessages);

            // Update in the database
            await updateSampleContactConversation({
                variables: {
                    id: selectedContact.id,
                    conversation: {
                        messages: postgresJsonbArray,
                    },
                },
            });

            // Refetch to ensure UI is updated
            await refetch();

            // Reset form and close modal
            setNewMessageForm({ text: '', subject: '', sender: 'me' });
            setShowAddMessageModal(false);
        } catch (error) {
            console.error('Failed to add message:', error);
        }
    };

    const [showDeleteDialog, setShowDeleteDialog] = useState<null | string>(null); // contactId or null
    const [deletingContactId, setDeletingContactId] = useState<string | null>(null);

    if (error) {
        return (
            <div className="flex flex-col flex-1 min-h-0 m-4 gap-4">
                <Card>
                    <CardContent className="p-6">
                        <p className="text-red-500 mb-4">{error.message}</p>
                        <Button onClick={() => refetch()}>Retry</Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="flex flex-1 min-h-0">
            {/* Contacts Sidebar */}
            <div className="w-80 border-r border-gray-200 flex flex-col bg-white">
                {/* Sidebar Header */}
                <div className="p-4 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                        <h2 className="text-lg font-semibold text-gray-900">Sample Conversations</h2>
                        <div className="flex gap-2">
                            <Button
                                onClick={() => setShowCreateContactModal(true)}
                                variant="outline"
                                size="sm"
                                className="bg-blue-500 text-white hover:bg-blue-600"
                            >
                                <Plus className="h-4 w-4 mr-1" />
                                New
                            </Button>
                            <Button onClick={() => refetch()} variant="ghost" size="sm">
                                <MessageCircle className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                    <p className="text-sm text-gray-600">{totalContacts} total contacts</p>
                </div>

                {/* Contacts List */}
                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="flex justify-center py-8">
                            <div className="text-gray-500">Loading contacts...</div>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-100">
                            {contacts.map((contact) => {
                                const lastMessage = getLastMessage(contact);
                                const contactName = getContactName(contact);
                                const isSelected = selectedContactId === contact.id;

                                return (
                                    <div
                                        key={contact.id}
                                        onClick={() => setSelectedContactId(contact.id)}
                                        className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                                            isSelected ? 'bg-blue-50 border-r-2 border-r-blue-500' : ''
                                        } relative`}
                                    >
                                        {/* Delete (X) Button */}
                                        <button
                                            className="absolute top-2 right-2 p-1 rounded-full hover:bg-red-100 text-gray-400 hover:text-red-600 z-10"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowDeleteDialog(contact.id);
                                            }}
                                            aria-label="Delete contact"
                                            disabled={deletingContactId === contact.id}
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                        <div className="flex items-start space-x-3">
                                            <div className="flex-shrink-0">
                                                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                                    <User className="h-5 w-5 text-blue-600" />
                                                </div>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between">
                                                    <p className="text-sm font-medium text-gray-900 truncate">
                                                        {contactName}
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        {lastMessage
                                                            ? formatTime(lastMessage.timestamp)
                                                            : formatTime(contact.createdAt)}
                                                    </p>
                                                </div>
                                                <p className="text-xs text-gray-600 truncate mb-1">
                                                    {contact.contact.email}
                                                </p>
                                                <p className="text-sm text-gray-600 truncate">
                                                    {lastMessage ? lastMessage.text : 'No messages yet'}
                                                </p>
                                                <div className="flex items-center mt-1">
                                                    <MessageCircle className="h-3 w-3 text-gray-400 mr-1" />
                                                    <span className="text-xs text-gray-500">
                                                        {contact.conversation?.messages?.length || 0} messages
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Pagination Footer */}
                {totalPages > 1 && (
                    <div className="p-3 border-t border-gray-200 bg-gray-50">
                        <div className="flex items-center justify-between">
                            <div className="text-xs text-gray-600">
                                Page {page} of {totalPages}
                            </div>
                            <div className="flex gap-1">
                                <Button onClick={handlePreviousPage} disabled={page === 1} variant="ghost" size="sm">
                                    <ChevronLeft className="h-3 w-3" />
                                </Button>
                                <Button
                                    onClick={handleNextPage}
                                    disabled={page === totalPages}
                                    variant="ghost"
                                    size="sm"
                                >
                                    <ChevronRight className="h-3 w-3" />
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Conversation View */}
            <div className="flex-1 flex flex-col bg-gray-50">
                {selectedContact ? (
                    <>
                        {/* Conversation Header */}
                        <div className="p-4 bg-white border-b border-gray-200 shadow-sm">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                    <User className="h-5 w-5 text-blue-600" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">
                                        {getContactName(selectedContact)}
                                    </h3>
                                    <p className="text-sm text-gray-600">{selectedContact.contact.email}</p>
                                </div>
                            </div>
                        </div>

                        {/* Messages */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-4">
                            {selectedContact.messages && selectedContact.messages.length > 0 ? (
                                [...selectedContact.messages]
                                    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
                                    .map((message, index) => {
                                        const isFromMe = isMessageFromMe(message, selectedContact.contact.email);
                                        return (
                                            <div
                                                key={index}
                                                className={`flex ${isFromMe ? 'justify-end' : 'justify-start'}`}
                                            >
                                                <div
                                                    className={`relative group p-3 rounded-lg max-w-xs md:max-w-md ${
                                                        isFromMe
                                                            ? 'bg-blue-500 text-white rounded-br-sm shadow-md'
                                                            : 'bg-white border border-gray-200 text-gray-900 rounded-bl-sm shadow-sm'
                                                    }`}
                                                >
                                                    {/* Edit Button */}
                                                    <button
                                                        onClick={() =>
                                                            setEditingMessage({
                                                                contactId: selectedContact.id,
                                                                messageIndex: index,
                                                                text: message.text,
                                                                subject: message.subject,
                                                            })
                                                        }
                                                        className={`absolute -top-2 -right-2 p-1 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity ${
                                                            isFromMe
                                                                ? 'bg-white text-blue-500 hover:bg-gray-50'
                                                                : 'bg-blue-500 text-white hover:bg-blue-600'
                                                        }`}
                                                    >
                                                        <Edit3 className="h-3 w-3" />
                                                    </button>
                                                    {message.subject && (
                                                        <div className="text-sm font-semibold mb-2 opacity-90">
                                                            {message.subject}
                                                        </div>
                                                    )}
                                                    <div className="text-sm leading-relaxed whitespace-pre-wrap">
                                                        {filterBlockquoteLines(message.text)}
                                                    </div>
                                                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200 border-opacity-20">
                                                        <div className="text-xs opacity-70">
                                                            <span className="font-medium">{message.sender}</span>
                                                            {message.recipients && message.recipients.length > 0 && (
                                                                <span>
                                                                    {' '}
                                                                    →{' '}
                                                                    {message.recipients.map((r) => r.email).join(', ')}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="text-xs opacity-70 flex items-center">
                                                            <Clock className="h-3 w-3 mr-1" />
                                                            {formatMessageTime(message.timestamp)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                            ) : (
                                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                                    <MessageCircle className="h-12 w-12 mb-4 opacity-50" />
                                    <p className="text-lg font-medium">No messages</p>
                                    <p className="text-sm">This contact has no conversation history</p>
                                </div>
                            )}

                            {/* Add Message Button */}
                            <div className="flex justify-center pt-4">
                                <Button
                                    onClick={() => setShowAddMessageModal(true)}
                                    className="bg-blue-500 hover:bg-blue-600 text-white"
                                >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add Message
                                </Button>
                            </div>
                        </div>
                    </>
                ) : (
                    /* No Contact Selected */
                    <div className="flex-1 flex items-center justify-center">
                        <div className="text-center text-gray-500">
                            <MessageCircle className="h-16 w-16 mx-auto mb-4 opacity-30" />
                            <h3 className="text-xl font-medium mb-2">Select a conversation</h3>
                            <p className="text-sm">Choose a contact from the sidebar to view their messages</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Message Editor */}
            {editingMessage && (
                <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
                    <div className="max-w-4xl mx-auto p-4">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-semibold text-gray-900">Edit Message</h3>
                            <Button onClick={handleCancelEdit} variant="ghost" size="sm">
                                <X className="h-4 w-4" />
                            </Button>
                        </div>

                        {/* Subject Line Editor (if message has subject) */}
                        {editingMessage.subject !== undefined && (
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                                <input
                                    type="text"
                                    value={editingMessage.subject || ''}
                                    onChange={(e) =>
                                        setEditingMessage({
                                            ...editingMessage,
                                            subject: e.target.value,
                                        })
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Subject line..."
                                />
                            </div>
                        )}

                        {/* Message Text Editor */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                            <textarea
                                value={editingMessage.text}
                                onChange={(e) =>
                                    setEditingMessage({
                                        ...editingMessage,
                                        text: e.target.value,
                                    })
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                rows={6}
                                placeholder="Edit your message..."
                            />
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end gap-2">
                            <Button onClick={handleCancelEdit} variant="outline">
                                Cancel
                            </Button>
                            <Button onClick={handleSaveMessage} className="bg-blue-500 hover:bg-blue-600">
                                <Save className="h-4 w-4 mr-2" />
                                Save Changes
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Contact Modal */}
            <Dialog open={showCreateContactModal} onOpenChange={setShowCreateContactModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Create New Contact</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="email">Email Address *</Label>
                            <Input
                                id="email"
                                type="email"
                                value={newContactForm.email}
                                onChange={(e) => setNewContactForm({ ...newContactForm, email: e.target.value })}
                                placeholder="contact@example.com"
                                required
                            />
                        </div>
                        <div>
                            <Label htmlFor="firstName">First Name</Label>
                            <Input
                                id="firstName"
                                type="text"
                                value={newContactForm.firstName}
                                onChange={(e) => setNewContactForm({ ...newContactForm, firstName: e.target.value })}
                                placeholder="John"
                            />
                        </div>
                        <div>
                            <Label htmlFor="lastName">Last Name</Label>
                            <Input
                                id="lastName"
                                type="text"
                                value={newContactForm.lastName}
                                onChange={(e) => setNewContactForm({ ...newContactForm, lastName: e.target.value })}
                                placeholder="Doe"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreateContactModal(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleCreateContact} disabled={!newContactForm.email.trim()}>
                            Create Contact
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Message Modal */}
            <Dialog open={showAddMessageModal} onOpenChange={setShowAddMessageModal}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Add New Message</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="sender">Sender</Label>
                            <Select
                                value={newMessageForm.sender}
                                onValueChange={(value) => setNewMessageForm({ ...newMessageForm, sender: value })}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select sender" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="me">Me</SelectItem>
                                    <SelectItem value="recipient">Recipient</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label htmlFor="subject">Subject (Optional)</Label>
                            <Input
                                id="subject"
                                type="text"
                                value={newMessageForm.subject}
                                onChange={(e) => setNewMessageForm({ ...newMessageForm, subject: e.target.value })}
                                placeholder="Enter subject line..."
                            />
                        </div>
                        <div>
                            <Label htmlFor="message">Message *</Label>
                            <textarea
                                id="message"
                                value={newMessageForm.text}
                                onChange={(e) => setNewMessageForm({ ...newMessageForm, text: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                rows={6}
                                placeholder="Enter your message..."
                                required
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAddMessageModal(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleAddMessage} disabled={!newMessageForm.text.trim()}>
                            Add Message
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Contact Confirmation Dialog */}
            <Dialog open={!!showDeleteDialog} onOpenChange={(open) => !open && setShowDeleteDialog(null)}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Delete Contact</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p>Are you sure you want to delete this contact? This action cannot be undone.</p>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setShowDeleteDialog(null)}
                            disabled={!!deletingContactId}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={async () => {
                                if (!showDeleteDialog) return;
                                setDeletingContactId(showDeleteDialog);
                                try {
                                    await deleteSampleContact({ variables: { id: showDeleteDialog } });
                                    setShowDeleteDialog(null);
                                    setDeletingContactId(null);
                                    // If the deleted contact was selected, clear selection
                                    if (selectedContactId === showDeleteDialog) {
                                        setSelectedContactId(null);
                                    }
                                    await refetch();
                                } catch (err) {
                                    setDeletingContactId(null);
                                    // Optionally show error
                                }
                            }}
                            disabled={!!deletingContactId}
                        >
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

// Page wrapper component that includes AdminHeader
export const SampleLibraryPage: FC = () => {
    return (
        <div className="flex h-full overflow-hidden">
            <div className="flex-1 flex flex-col min-w-0">
                <AdminHeader />
                <SampleLibraryContent />
            </div>
        </div>
    );
};

export default SampleLibraryPage;
